import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Circle,
  MoreHorizontal,
  Calendar,
  Clock
} from 'lucide-react';
import RefreshButton from '../ui/RefreshButton';
import { t } from 'i18next';

// Color configurations remain unchanged
const COLOR_VARIANTS = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/50',
    text: 'text-blue-600 dark:text-blue-400',
    icon: 'text-blue-500 dark:text-blue-300',
    progressBg: 'bg-blue-200 dark:bg-blue-800',
    progressFill: 'bg-blue-600 dark:bg-blue-400',
    hoverBg: 'hover:bg-blue-100 dark:hover:bg-blue-800/60'
  },
  // El resto de variantes de color permanecen igual
  purple: {
    bg: 'bg-purple-50 dark:bg-purple-900/50',
    text: 'text-purple-600 dark:text-purple-400',
    icon: 'text-purple-500 dark:text-purple-300',
    progressBg: 'bg-purple-200 dark:bg-purple-800',
    progressFill: 'bg-purple-600 dark:bg-purple-400',
    hoverBg: 'hover:bg-purple-100 dark:hover:bg-purple-800/60'
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/50',
    text: 'text-green-600 dark:text-green-400',
    icon: 'text-green-500 dark:text-green-300',
    progressBg: 'bg-green-200 dark:bg-green-800',
    progressFill: 'bg-green-600 dark:bg-green-400',
    hoverBg: 'hover:bg-green-100 dark:hover:bg-green-800/60'
  },
  // ... otras variantes de color
};

// Types remain unchanged
type TrendDirection = 'up' | 'down' | 'neutral';
type ColorVariant = keyof typeof COLOR_VARIANTS;
type TimeRange = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

// Animation variants remain unchanged
const cardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  hover: { scale: 1.02, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" }
};

const progressVariants = {
  initial: { width: 0 },
  animate: (progress: number) => ({
    width: `${Math.min(Math.max(progress, 0), 100)}%`,
    transition: { duration: 0.8, type: 'spring', stiffness: 50 }
  })
};

const countupAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } }
};

const pulseAnimation = {
  initial: { scale: 1 },
  animate: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse" as const
    }
  }
};

// Interface con propiedades de limitación de refresco
interface ProgressStatsProps {
  title: string;
  value: number | string;
  label: string;
  trend: {
    percentage: number;
    direction: TrendDirection;
  };
  color?: ColorVariant;
  progress?: number;
  icon?: React.ElementType;
  comparison?: string;
  timeRange?: TimeRange;
  additionalStats?: Array<{
    label: string;
    value: string | number;
  }>;
  isLoading?: boolean;
  animate?: boolean;
  onRefresh?: () => void;
  pulseEffect?: boolean;
  showDetailedTrend?: boolean;
  refreshCooldown?: number;
  maxRefreshCount?: number;
  refreshTimeWindow?: number;
}

export function ProgressStats({
  title,
  value,
  label,
  trend,
  color = 'blue',
  progress,
  icon: IconComponent = TrendingUp,
  comparison,
  timeRange = 'month',
  additionalStats = [],
  isLoading = false,
  animate = true,
  onRefresh,
  pulseEffect = false,
  showDetailedTrend = false,
  // Parámetros para el RefreshButton
  refreshCooldown,
  maxRefreshCount,
  refreshTimeWindow
}: ProgressStatsProps) {
  const [displayValue, setDisplayValue] = useState<number | string>(typeof value === 'number' ? 0 : value);
  const [isHovered, setIsHovered] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Determine trend icon and color
  const getTrendIcon = () => {
    switch (trend.direction) {
      case 'up':
        return ArrowUpRight;
      case 'down':
        return ArrowDownRight;
      default:
        return Circle;
    }
  };

  const TrendIcon = getTrendIcon();
  const colorVariant = COLOR_VARIANTS[color];

  // Time range icon
  const getTimeRangeIcon = () => {
    switch (timeRange) {
      case 'day':
        return Clock;
      default:
        return Calendar;
    }
  };

  const TimeRangeIcon = getTimeRangeIcon();

  // Animation for numeric value
  useEffect(() => {
    if (animate && typeof value === 'number' && typeof displayValue === 'number') {
      const duration = 1000; // ms
      const steps = 20;
      const stepDuration = duration / steps;
      const increment = (value - displayValue) / steps;

      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;

        if (currentStep >= steps) {
          setDisplayValue(value);
          clearInterval(timer);
        } else {
          setDisplayValue((prev) =>
            typeof prev === 'number' ? +(prev + increment).toFixed(1) : prev
          );
        }
      }, stepDuration);

      return () => clearInterval(timer);
    } else {
      setDisplayValue(value);
    }
  }, [value, animate]);

  // Format the time range for display
  const formatTimeRange = () => {
    switch (timeRange) {
      case 'day':
        return t('common.day');
      case 'week':
        return t('common.thisWeek');
      case 'month':
        return t('common.thisMonth');
      case 'quarter':
        return t('common.thisQuarter');
      case 'year':
        return t('common.thisYear');
      case 'custom':
        return t('common.customize');
      default:
        return t('common.thisMonth');
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      whileHover={pulseEffect ? "hover" : undefined}
      transition={{ duration: 0.3 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`
        bg-white dark:bg-gray-800 
        rounded-xl shadow-lg
        transition-all duration-300 
        border border-gray-100 dark:border-gray-700
        p-4 sm:p-5 space-y-3
        relative
        overflow-hidden
      `}
    >
      {/* Optional pulse effect overlay */}
      {pulseEffect && (
        <motion.div
          variants={pulseAnimation}
          initial="initial"
          animate="animate"
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-10 pointer-events-none"
        />
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2 max-w-[70%]">
          <h3 className="
            text-xs sm:text-sm 
            font-medium 
            text-gray-500 dark:text-gray-400 
            uppercase tracking-wider 
            truncate
          ">
            {title}
          </h3>
          {timeRange && (
            <div className="hidden sm:flex items-center text-[10px] text-gray-400 dark:text-gray-500">
              <TimeRangeIcon className="w-3 h-3 mr-1" />
              <span>{formatTimeRange()}</span>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-1">
          {/* Usamos el componente RefreshButton en lugar de la implementación anterior */}
          {onRefresh && (
            <RefreshButton
              onRefresh={onRefresh}
              isLoading={isLoading}
              size="sm"
              cooldownTime={refreshCooldown}
              maxRefreshCount={maxRefreshCount}
              timeWindow={refreshTimeWindow}
            />
          )}

          {IconComponent && (
            <IconComponent
              className={`w-4 h-4 sm:w-5 sm:h-5 ${colorVariant.icon}`}
            />
          )}
        </div>
      </div>

      {/* Value */}
      <motion.div
        variants={countupAnimation}
        initial="initial"
        animate="animate"
        className="flex items-baseline space-x-2 overflow-hidden"
      >
        <p className={`
          text-2xl sm:text-3xl 
          font-bold 
          ${colorVariant.text}
          truncate
        `}>
          {displayValue}
        </p>
        <p className="
          text-xs sm:text-sm 
          text-gray-500 dark:text-gray-400 
          truncate
        ">
          {label}
        </p>
      </motion.div>

      {/* Trend Indicator */}
      <div className="flex items-center space-x-2 flex-wrap gap-2">
        <motion.div
          whileHover={{ scale: 1.05 }}
          className={`
            inline-flex items-center 
            px-2 sm:px-2.5 py-0.5 sm:py-1 
            rounded-full 
            text-[10px] sm:text-xs 
            font-medium
            cursor-pointer
            ${colorVariant.bg} ${colorVariant.text} ${colorVariant.hoverBg}
          `}
          onClick={() => setShowDetails(!showDetails)}
        >
          <TrendIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
          <span>{trend.percentage}%</span>
        </motion.div>

        {comparison && (
          <span className="
            text-[10px] sm:text-xs 
            text-gray-500 dark:text-gray-400 
            truncate
          ">
            {comparison}
          </span>
        )}

        {additionalStats.length > 0 && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowDetails(!showDetails)}
            className="ml-auto p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </motion.button>
        )}
      </div>

      {/* Optional Progress Bar */}
      {progress !== undefined && (
        <div className={`h-1.5 sm:h-2 w-full rounded-full ${colorVariant.progressBg} overflow-hidden`}>
          <motion.div
            variants={progressVariants}
            initial="initial"
            animate="animate"
            custom={progress}
            className={`h-full ${colorVariant.progressFill}`}
          />
        </div>
      )}

      {/* Detailed trend info */}
      <AnimatePresence>
        {showDetails && additionalStats.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden mt-2 pt-2 border-t border-gray-100 dark:border-gray-700"
          >
            <div className="grid grid-cols-2 gap-2">
              {additionalStats.map((stat, index) => (
                <div key={index} className="flex flex-col">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">{stat.label}</span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{stat.value}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detailed trend visualization */}
      <AnimatePresence>
        {showDetailedTrend && showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="pt-3 mt-2 border-t border-gray-100 dark:border-gray-700"
          >
            <div className="h-16 flex items-end space-x-1">
              {[...Array(12)].map((_, i) => {
                const height = 30 + Math.random() * 70;
                return (
                  <motion.div
                    key={i}
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: i * 0.05, duration: 0.5 }}
                    className={`flex-1 rounded-t ${colorVariant.progressFill} opacity-${Math.round((i / 12) * 100)}`}
                  />
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default ProgressStats;